import { LanguageService } from '@app/core/services/language.service';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SubSink } from 'subsink';
import { IonicModule } from '@ionic/angular';
import { FormlyAttributes } from '@ngx-formly/core';
import { LocaleNumberPipe } from '@app/pipes/locale-number.pipe';
import { Constants } from '@app/core/services/constants';
import { AuthService } from '@app/core/services/auth.service';

interface CurrencyValue {
  unit: string;
  value: number | undefined;
}


@Component({
  selector: 'app-formly-field-currency',
  templateUrl: './formly-field-currency.component.html',
  styleUrls: ['./formly-field-currency.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    FormlyAttributes,
    LocaleNumberPipe,
  ],
})
export class FormlyFieldCurrencyComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  public currencyCode: string = Constants.defaultCurrencyCode;
  public currencyCodes = [...Constants.mainCurrencies];//, ...Intl.supportedValuesOf("currency")];
  public currencyNames: any = {};
  public defaultCurrencyCode = Constants.defaultCurrencyCode;
  public currencyValue: number | undefined = undefined;
  private subsink = new SubSink();
  
  constructor(private languageService: LanguageService,
              private authService: AuthService,
              private changeDetectorRef: ChangeDetectorRef) {
    super();
  }
  
  ngOnInit(): void {
    this.defaultCurrencyCode = this.authService.currentUserValue?.jsonPayload.currencyCode || Constants.defaultCurrencyCode;
    this.subsink.sink = this.languageService.currentLanguage$.subscribe(locale => {
      this.getCurrencyNames(locale);
    });
    // Subscribe to formControl value changes to update localValue
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });
    
    // Initialize with current value
    this.updateLocalValue(this.formControl.value);
  }

  private updateLocalValue(modelValue: any): void {    
    if (modelValue && typeof modelValue === 'object') {
      // Handle object format { unit: MileageUnit, value: number }
      this.currencyCode = modelValue.unit || (Constants.defaultCurrencyCode as string);
      this.currencyValue = modelValue.value;
    } else if (typeof modelValue === 'number') {
      // Handle legacy number format
      this.currencyValue = modelValue;
      this.currencyCode = (this.field.props['currencyCode'] || Constants.defaultCurrencyCode) as string;
    } else {
      // Default values
      this.currencyCode = (this.field.props['currencyCode'] || Constants.defaultCurrencyCode) as string;
      this.currencyValue = undefined;
    }
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe(); 
  }

  private getCurrencyNames(locale: string) {
    const translator = new Intl.DisplayNames([locale], { type: 'currency' });
    for (const code of this.currencyCodes) {
      if (code === 'spacer') {
        this.currencyNames[code] = '______________________';
        continue;
      }
      this.currencyNames[code] = translator?.of(code) || code;
    } 
  }

  onCurrencyValueChange(newValue: number | undefined): void {
    // const value = parseFloat(event.target?.value || event.detail?.value || 0);
    this.currencyValue = newValue;
    this.updateFormControl();
  }

  onCurrencyCodeChange(code: string): void {
    this.currencyCode = code;
    this.updateFormControl();
  }

  private updateFormControl(): void {
    const currencyObject: CurrencyValue = {
      unit: this.currencyCode,
      value: this.currencyValue
    };

    this.formControl.setValue(currencyObject);
    this.formControl.markAsDirty();
    
    // Trigger onChange callbacks if defined
    this.onChange(currencyObject);
    this.changeDetectorRef.markForCheck();
  }
}
